import { API, graphqlOperation } from 'aws-amplify';
import { getCognitoUserSessions } from '../../graphql/';

interface SessionValidationResult {
  isValid: boolean;
  error?: string;
  retryCount?: number;
}

export const validateUserSession = async (maxRetries: number = 5): Promise<SessionValidationResult> => {
  const currentSession = localStorage.getItem('deviceKey');

  if (!currentSession) {
    console.log('No sessionID found in localStorage');
    return { isValid: false, error: 'No session ID found' };
  }

  console.log('Validating session:', currentSession);

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Session validation attempt ${attempt}/${maxRetries}`);

      const sessions = (await API.graphql(graphqlOperation(getCognitoUserSessions))) as any;

      const userSessions = sessions.data?.getCognitoUserSessions;

      if (!userSessions || !Array.isArray(userSessions)) {
        console.log(`Attempt ${attempt}: No sessions array returned`);
        return { isValid: false, error: 'No sessions data returned', retryCount: attempt };
      }

      if (userSessions.length === 0) {
        console.log(`Attempt ${attempt}: Empty sessions array`);
        return { isValid: false, error: 'No active sessions found', retryCount: attempt };
      }

      // Check if current session exists in the array
      const sessionExists = userSessions.find((session) => session?.linkedsessionid === currentSession);
      console.log({ userSessions, currentSession });
      if (sessionExists) {
        console.log(`Attempt ${attempt}: Session validation successful`);
        return { isValid: true, retryCount: attempt };
      } else {
        console.log(`Attempt ${attempt}: Session not found in active sessions`);
        return { isValid: false, error: 'Session not found in active sessions', retryCount: attempt };
      }
    } catch (error: any) {
      console.error(`Attempt ${attempt}: Session validation failed:`, error);

      // Check if it's a network error or server error that we should retry
      const shouldRetry =
        error?.response?.status >= 500 || // Server errors (500+)
        error?.code === 'NetworkError' || // Network errors
        error?.message?.includes('Network') || // Network-related errors
        error?.message?.includes('timeout') || // Timeout errors
        !error?.response; // No response (network issues)

      if (shouldRetry && attempt < maxRetries) {
        console.log(`Attempt ${attempt}: Retrying in 1 second...`);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        continue;
      } else {
        // If it's a 401 (unauthorized) or we've exhausted retries, return false
        if (error?.response?.status === 401) {
          console.log(`Attempt ${attempt}: 401 Unauthorized - stopping retries`);
          return { isValid: false, error: 'Unauthorized (401)', retryCount: attempt };
        }

        console.log(`Attempt ${attempt}: Max retries reached or non-retryable error`);
        return {
          isValid: false,
          error: error?.message || 'Unknown error',
          retryCount: attempt,
        };
      }
    }
  }

  // This should never be reached, but just in case
  return { isValid: false, error: 'Max retries exceeded', retryCount: maxRetries };
};

// Usage example:
// const result = await validateUserSession();
// if (!result.isValid) {
//   console.log('Session invalid:', result.error);
//   // Handle invalid session (redirect to login, etc.)
// }
